import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import { emarsysCodes } from "@components/Newsletter/types/Emarsys";
import { BT_NEWSLETTERS } from "@components/Newsletter/types/Newsletter";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getSubscribeStatusDescription,
  getSubscribeStatusHeading,
} from "@pages/Newsletter/Subscribe/utils/helpers";
import { StatusEnums } from "@typings/enums";
import axios, { HttpStatusCode } from "axios";

/** The main component for the newsletter subscribe page. */
const NewsletterSubscribePage = (): React.ReactElement => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(StatusEnums.Loading);

  const edition = searchParams.get("edition")?.toString();
  const userid = searchParams.get("userid")?.toString();

  const heading = getSubscribeStatusHeading(status);
  const description = getSubscribeStatusDescription(status);

  useEffect(() => {
    if (typeof userid === "undefined" || typeof edition === "undefined") {
      setStatus(StatusEnums.Error);
      return;
    }

    const subscriptionProcess = async () => {
      const getContactData = async () => {
        const payload = {
          service: "emarsysv2",
          payload: {
            endpoint: "getContactData",
            fields: ["id", "uid", 3],
            keyId: "uid",
            keyValues: [userid],
          },
        };

        const response = await axios.post("/_plat/api/v1/bff", payload, {
          timeout: 10000,
        });

        const data = response.data.body;

        if (!data.success) {
          console.error("getContactData error", data); // eslint-disable-line no-console
          setStatus(StatusEnums.Error);
          return null;
        }

        return data.data;
      };

      const subscribeUser = async (email: string) => {
        // We did this way so we can reuse the existing newsletter code.
        const selectedKeys = BT_NEWSLETTERS.filter(
          (newsletter) => newsletter.emarsysFieldId === edition
        ).map((newsletter) => newsletter.type);

        const apiPayload = {
          service: "emarsys",
          payload: {
            email: email,
            selectedKeys: selectedKeys,
          },
        };

        const response = await axios.post("/_plat/api/v1/bff", apiPayload, {
          timeout: 10000,
        });

        const data = response.data;

        if (data.statusCode !== HttpStatusCode.Ok) {
          console.error("subscribeUser: Status code returned is not 200", data); // eslint-disable-line no-console
          setStatus(StatusEnums.Error);
          return;
        }

        if (!emarsysCodes.includes(data.body)) {
          console.error("subscribeUser: Emarsys code is not valid", data); // eslint-disable-line no-console
          setStatus(StatusEnums.Error);
          return;
        }

        setStatus(StatusEnums.Success);
      };

      const contactData = await getContactData();

      if (contactData) {
        await subscribeUser(contactData?.[0]?.[3]);
      }
    };

    subscriptionProcess();
  }, [userid, edition]);

  return (
    <div data-testid="newsletter-subscribe-page-component" className="w-full">
      <AdSettings
        adNames={["lb1", "prestitial"]}
        path={RouteFactory.newsletterSubscribe}
      />

      <MetaTags
        title="THE BUSINESS TIMES - Get the Latest Business & Financial News"
        description="Read more at The Business Times. UnSubscriber Here."
        slug={RouteFactory.newsletterSubscribe}
      />

      <GAData title="Newsletter Subscribe" level2="newsletter" />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <Container rootClassName="py-12 text-center">
        <>
          {status === StatusEnums.Loading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              size="4x"
              color="text-white"
              spin
              className="mb-4"
            />
          ) : null}

          <h2 className="mb-4 text-center font-lct text-4xl font-bold">
            {heading}
          </h2>

          <p className="text-center font-public-sans text-base font-normal">
            {description}
          </p>
        </>
      </Container>
    </div>
  );
};

export default NewsletterSubscribePage;
